import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  UserIcon,
  MailIcon,
  CalendarIcon,
  ShieldIcon,
  CheckCircleIcon,
  XCircleIcon,
  Loader2,
} from 'lucide-react';
import { getCountryTranslation } from '../../services/countriesTranslationsService';
import { getRegionTranslation } from '../../services/regionsTranslationsService';
import { getDistrictTranslation } from '../../services/districtsTranslationsService';
import { getInstitutionTranslation } from '../../services/institutionsTranslationsService';
import { getDepartmentTranslation } from '../../services/departmentsTranslationsService';

const ORG_ACCESS_NAME_FIELDS = ['country_name', 'region_name', 'district_name', 'institution_name', 'department_name'];

const isCompleteOrganizationalAccessEntry = (org) => {
  if (!org || typeof org !== 'object') return false;
  return ORG_ACCESS_NAME_FIELDS.every((key) => {
    const v = org[key];
    return v != null && String(v).trim() !== '';
  });
};

const Profile = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isRTL, currentLanguage } = useLanguage();

  const getRoleIcon = (roleName) => {
    switch (roleName) {
      case 'super_admin':
        return <ShieldIcon className="h-5 w-5 text-rose-600" aria-hidden />;
      case 'admin':
        return <ShieldIcon className="h-5 w-5 text-violet-600" aria-hidden />;
      case 'manager':
        return <ShieldIcon className="h-5 w-5 text-sky-600" aria-hidden />;
      case 'supervisor':
        return <ShieldIcon className="h-5 w-5 text-emerald-600" aria-hidden />;
      case 'trainer':
        return <ShieldIcon className="h-5 w-5 text-amber-600" aria-hidden />;
      default:
        return <UserIcon className="h-5 w-5 text-slate-600" aria-hidden />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('profile.notAvailable');
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : currentLanguage === 'ar' ? 'ar-EG' : 'fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fieldBox = 'rounded-xl border-[2px] border-slate-200/90 bg-gradient-to-br from-white to-slate-50/90 px-3 py-2.5 text-sm font-semibold text-slate-900';

  const completeOrganizationalAccess = (user?.organizationalAccess ?? []).filter(isCompleteOrganizationalAccessEntry);

  if (!user) {
    return (
      <div className="mx-auto w-full max-w-6xl px-3 py-10 sm:px-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="rounded-[1.35rem] border-[3px] border-sky-200/80 bg-gradient-to-br from-white to-sky-50/80 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(14,165,233,0.1)]">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-sky-600" aria-hidden strokeWidth={2.25} />
          <p className="mt-4 text-sm font-bold text-slate-600 sm:text-base">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const initials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.trim() || '?';

  return (
    <div
      className="mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <header className="relative mt-5 overflow-hidden rounded-[1.5rem] border-[3px] border-sky-300/70 bg-gradient-to-br from-sky-100 via-amber-50/90 to-emerald-50 px-4 py-5 shadow-[0_8px_0_0_rgba(14,165,233,0.12)] sm:mt-6 sm:rounded-[1.75rem] sm:px-7 sm:py-6">
        <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-amber-200/45 blur-2xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-8 -left-4 h-24 w-32 rounded-full bg-sky-200/35 blur-2xl" aria-hidden />
        <div className="relative">
          <div className={`flex flex-wrap items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border-[3px] border-white bg-gradient-to-br from-sky-400 to-violet-500 text-lg font-black text-white shadow-md sm:h-[4.25rem] sm:w-[4.25rem] sm:text-xl">
              {initials}
            </div>
            <div className={`min-w-0 flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h2 className="text-xl font-black tracking-tight text-slate-900 sm:text-2xl">
                {user?.firstName} {user?.lastName}
              </h2>
              <p className="mt-0.5 text-sm font-bold text-slate-600">
                {user?.roles?.[0] ? t(`roles.${user.roles[0].name.toUpperCase()}`) : t('profile.notAvailable')}
              </p>
              <p
                className={`mt-1 flex items-center gap-1.5 truncate text-xs font-medium text-slate-500 sm:text-sm ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <MailIcon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                {user?.email}
              </p>
            </div>
          </div>
        </div>
      </header>

      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
        <div className="border-b border-slate-200/90 bg-gradient-to-r from-amber-50/90 via-white to-violet-50/50 px-4 py-3 sm:px-5">
          <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">{t('profile.personalInfo')}</h2>
        </div>
        <div className="space-y-6 p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                {t('profile.firstName')}
              </label>
              <div className={fieldBox}>{user?.firstName || t('profile.notAvailable')}</div>
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                {t('profile.lastName')}
              </label>
              <div className={fieldBox}>{user?.lastName || t('profile.notAvailable')}</div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                {t('common.email')}
              </label>
              <div className={fieldBox}>{user?.email || t('profile.notAvailable')}</div>
            </div>
          </div>
        </div>
      </section>

      {completeOrganizationalAccess.length > 0 && (
        <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
          <div className="border-b border-slate-200/90 bg-gradient-to-r from-violet-50/90 via-white to-sky-50/50 px-4 py-3 sm:px-5">
            <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">{t('profile.organizationalAccess')}</h2>
          </div>
          <div className="space-y-3 p-4 sm:p-5">
            {completeOrganizationalAccess.map((org, index) => (
              <div
                key={index}
                className="rounded-xl border-[3px] border-slate-200/80 bg-gradient-to-br from-slate-50/90 to-white p-4 shadow-sm"
              >
                <div className="grid gap-2 text-sm">
                  <div className={`flex justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="font-bold text-slate-600">{t('profile.country')}</span>
                    <span className="text-end font-semibold text-slate-900">
                      {getCountryTranslation(org.country_name, currentLanguage)}
                    </span>
                  </div>
                  <div className={`flex justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="font-bold text-slate-600">{t('profile.region')}</span>
                    <span className="text-end font-semibold text-slate-900">
                      {getRegionTranslation(org.region_name, currentLanguage)}
                    </span>
                  </div>
                  <div className={`flex justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="font-bold text-slate-600">{t('profile.district')}</span>
                    <span className="text-end font-semibold text-slate-900">
                      {getDistrictTranslation(org.district_name, currentLanguage)}
                    </span>
                  </div>
                  <div className={`flex justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="font-bold text-slate-600">{t('profile.institution')}</span>
                    <span className="text-end font-semibold text-slate-900">
                      {getInstitutionTranslation(org.institution_name, currentLanguage)}
                    </span>
                  </div>
                  <div className={`flex justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="font-bold text-slate-600">{t('profile.department')}</span>
                    <span className="text-end font-semibold text-slate-900">
                      {getDepartmentTranslation(org.department_name, currentLanguage)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
        <div className="border-b border-slate-200/90 bg-gradient-to-r from-emerald-50/80 via-white to-sky-50/40 px-4 py-3 sm:px-5">
          <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">{t('profile.accountInfo')}</h2>
        </div>
        <div className="space-y-6 p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                {t('profile.username')}
              </label>
              <div className={fieldBox}>{user?.username || t('profile.notAvailable')}</div>
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                {t('profile.userId')}
              </label>
              <div className={fieldBox}>#{user?.id || t('profile.notAvailable')}</div>
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                {t('profile.accountStatus')}
              </label>
              <div className={`${fieldBox} flex items-center gap-2`}>
                {user?.isActive ? (
                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                ) : (
                  <XCircleIcon className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                )}
                <span className={user?.isActive ? 'text-emerald-800' : 'text-rose-800'}>
                  {user?.isActive ? t('profile.active') : t('profile.inactive')}
                </span>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                {t('profile.verificationStatus')}
              </label>
              <div className={`${fieldBox} flex items-center gap-2`}>
                {user?.isVerified ? (
                  <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                ) : (
                  <XCircleIcon className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />
                )}
                <span className={user?.isVerified ? 'text-emerald-800' : 'text-rose-800'}>
                  {user?.isVerified ? t('profile.verified') : t('profile.unverified')}
                </span>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                {t('profile.lastLogin')}
              </label>
              <div className={`${fieldBox} flex items-center gap-2`}>
                <CalendarIcon className="h-4 w-4 shrink-0 text-sky-500" aria-hidden />
                {user?.lastLogin ? formatDate(user.lastLogin) : t('profile.notAvailable')}
              </div>
            </div>
          </div>

          {user?.roles && user.roles.length > 0 && (
            <div className="border-t border-slate-200/80 pt-6">
              <h3 className={`mb-4 flex items-center gap-2 text-sm font-black text-slate-900 sm:text-base ${isRTL ? 'flex-row-reverse' : ''}`}>
                <ShieldIcon className="h-5 w-5 shrink-0 text-violet-600" aria-hidden />
                {t('profile.roles')}
              </h3>
              <div className="space-y-3">
                {user.roles.map((role, index) => (
                  <div
                    key={index}
                    className="rounded-xl border-[3px] border-slate-200/80 bg-gradient-to-br from-violet-50/50 to-white p-4 shadow-sm"
                  >
                    <div className={`flex flex-wrap items-start justify-between gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`flex min-w-0 items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        {getRoleIcon(role.name)}
                        <div className={`min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                          <h5 className="text-sm font-bold text-slate-900">{t(`roles.${role.name.toUpperCase()}`)}</h5>
                          {role.description && <p className="mt-1 text-xs text-slate-600">{role.description}</p>}
                        </div>
                      </div>
                      <span className="inline-flex shrink-0 items-center rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-900">
                        {t('profile.level')} {role.level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Profile;
